import axios from 'axios'
import { ElLoading } from 'element-plus'
import apiConfig, { buildApiUrl, resolveRequestTimeout } from './config'

const DEFAULT_ERROR_MESSAGE = '请求失败，请稍后重试'
const NETWORK_ERROR_MESSAGE = '网络连接失败，请检查网络后重试'
const TIMEOUT_ERROR_MESSAGE = '请求超时，请稍后重试'

const service = axios.create({
  // 请求超时时间
  timeout: apiConfig.defaultTimeout
})

let loadingCount = 0
// 开启加载
let loadingInstance = null

const showLoading = (config = {}) => {
  if (config.noloading) {
    return
  }
  if (loadingCount === 0) {
    loadingInstance = ElLoading.service({
      lock: true,
      text: '加载中...',
      customClass: 'm-loading-page',
      background: 'rgba(0, 0, 0, 0.3)'
    })
  }
  loadingCount += 1
}

const closeLoading = (config = {}) => {
  if (config.noloading) {
    return
  }
  if (loadingCount <= 0) {
    return
  }
  loadingCount -= 1
  if (loadingCount === 0 && loadingInstance) {
    loadingInstance.close()
    loadingInstance = null
  }
}

// 历史 POST 接口的成功判定并不完全一致：
// 有的返回 Success，有的则依赖 Coordinates 是否存在，这里统一兼容旧协议。
const isPlainObjectResponseAllowed = (data, config = {}) => {
  if (data?.Success === false || data?.success === false) {
    return false
  }

  return Boolean(
    config?.allowPlainObjectResponse
    && data
    && typeof data === 'object'
    && !Array.isArray(data)
  )
}

const isArrayResponseAllowed = (data, config = {}) => {
  return Boolean(
    config?.allowArrayResponse
    && Array.isArray(data)
  )
}

const isPostSuccess = (data, config = {}) => {
  if (typeof data?.Success === 'boolean') {
    return data.Success
  }

  if (typeof data?.success === 'boolean') {
    return data.success
  }

  if (data && data.Success) {
    return true
  }

  // Coordinate.batchTransform 后端成功时不返回 Success 字段，只返回 Coordinates 数组。
  // 移除此分支会导致坐标转换功能直接走 reject，不可删除。
  if (Array.isArray(data?.Coordinates) && data.Coordinates.length > 0) {
    return true
  }

  return isPlainObjectResponseAllowed(data, config) || isArrayResponseAllowed(data, config)
}

const isGetSuccess = (data, config = {}) => {
  if (typeof data?.Success === 'boolean') {
    return data.Success
  }

  if (typeof data?.success === 'boolean') {
    return data.success
  }

  if (data?.Success) {
    return true
  }

  return isPlainObjectResponseAllowed(data, config) || isArrayResponseAllowed(data, config)
}

const getResponseMessage = (payload, fallbackMessage = DEFAULT_ERROR_MESSAGE) => {
  return payload?.Message || payload?.message || payload?.msg || fallbackMessage
}

const isFormDataPayload = (payload) => {
  return typeof FormData !== 'undefined' && payload instanceof FormData
}

// 将网络错误、超时、HTTP 错误和业务错误统一收敛成同一结构，
// 这样业务层 catch 时就不会再遇到 undefined 或字段名不一致的问题。
const normalizeRequestError = (error, payload, response) => {
  const status = response?.status ?? error?.response?.status ?? payload?.StatusCode ?? payload?.status ?? null
  const code = error?.code || payload?.code || null

  let type = 'business'
  if (code === 'ECONNABORTED') {
    type = 'timeout'
  } else if (!error?.response && error) {
    type = 'network'
  } else if (status >= 500) {
    type = 'server'
  } else if (status >= 400) {
    type = 'http'
  }

  const fallbackMessage = type === 'timeout'
    ? TIMEOUT_ERROR_MESSAGE
    : (type === 'network' ? NETWORK_ERROR_MESSAGE : DEFAULT_ERROR_MESSAGE)
  const message = getResponseMessage(payload, error?.message || fallbackMessage)
  const normalizedPayload = payload && typeof payload === 'object' ? payload : {}

  return {
    ...normalizedPayload,
    type,
    status,
    code,
    message,
    Message: normalizedPayload.Message || message,
    success: false,
    Success: false,
    raw: payload ?? error ?? null
  }
}

const isRequestSuccessful = (method, data, config = {}) => {
  if (method === 'post') {
    return isPostSuccess(data, config)
  }

  return isGetSuccess(data, config)
}

// 请求层只在失败分支做结构归一；
// 成功分支继续返回后端原始 payload，尽量不打破现有业务读取 res.Data / res.Success 的方式。
const executeRequest = (method, url, params, config = {}, headers) => {
  showLoading(config)

  let requestPromise
  try {
    const requestConfig = {
      headers: {
        ...(headers || {})
      },
      timeout: resolveRequestTimeout(config.timeout),
      signal: config.signal
    }

    if (isFormDataPayload(params)) {
      delete requestConfig.headers['Content-Type']
      delete requestConfig.headers['content-type']
    } else if (!requestConfig.headers['Content-Type'] && !requestConfig.headers['content-type']) {
      requestConfig.headers['Content-Type'] = 'application/json;charset=UTF-8'
    }

    requestPromise = method === 'get'
      ? service.get(buildApiUrl(url), {
        ...requestConfig,
        params
      })
      : service.post(
        buildApiUrl(url),
        config?.is_array ? params?.list : params,
        requestConfig
      )
  } catch (e) {
    // buildApiUrl / resolveRequestTimeout 等同步代码抛异常时，确保 loading 归零。
    closeLoading(config)
    return Promise.reject(normalizeRequestError(e, null, null))
  }

  return new Promise((resolve, reject) => {
    requestPromise
      .then((response) => {
        closeLoading(config)
        const responseData = response?.data

        if (response?.status === 200 && isRequestSuccessful(method, responseData, config)) {
          resolve(responseData)
          return
        }

        reject(normalizeRequestError(null, responseData, response))
      })
      .catch((error) => {
        closeLoading(config)
        const responseData = error?.response?.data

        // 兼容某些后端接口在非 2xx 状态码下仍返回有效业务数据的情况
        // （如 Coordinate.batchTransform 可能在特定边界条件下返回非 200 但带 Coordinates 数据）。
        // 无法确认已无依赖场景，故保留此回退逻辑。
        if (isRequestSuccessful(method, responseData, config)) {
          resolve(responseData)
          return
        }

        reject(normalizeRequestError(error, responseData, error?.response))
      })
  })
}

export function get(url, params, config, headers) {
  return executeRequest('get', url, params, config, headers)
}

export function post(url, params, config, headers) {
  return executeRequest('post', url, params, config, headers)
}
